import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  useTheme,
} from '@mui/material';
import { useAlert } from '@schamburg-web/components';
import React, { useEffect, useState } from 'react';
import WarningMessage from '../shared/components/WarningMessage/WarningMessage';
import { changePassword } from '../util';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const theme = useTheme();
  const [warningMessage, setWarningMessage] = useState('');
  const { showAlert } = useAlert();

  useEffect(() => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setWarningMessage('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setWarningMessage('New passwords do not match.');
      return;
    }
    setWarningMessage('');
    // Cleanup function to reset warning state
    return () => {
      setWarningMessage('');
    };
  }, [confirmPassword, newPassword, oldPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdLoading(true);
    // Replace with real API call
    const result = await changePassword(oldPassword, newPassword);
    setPwdLoading(false);
    if (result.success === false) {
      setWarningMessage(result.exception || 'Failed to change password.');
    } else {
      showAlert('Account updated successfully!', 'success');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : '#fff',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 4px 24px rgba(0,0,0,0.7)'
                : '0 4px 24px rgba(0,0,0,0.15)',
            minWidth: { xs: '90vw', sm: 400 },
            p: 0,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          pb: 0,
        }}
      >
        Change Password
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <form>
          <PasswordField
            label="Current Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            show={showOldPassword}
            setShow={setShowOldPassword}
            autoFocus
          />
          <PasswordField
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            show={showNewPassword}
            setShow={setShowNewPassword}
          />
          <PasswordField
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            show={showConfirmPassword}
            setShow={setShowConfirmPassword}
          />
          <WarningMessage
            message={warningMessage}
            show={warningMessage !== ''}
          />
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={pwdLoading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          disabled={warningMessage !== '' || pwdLoading}
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          {pwdLoading ? 'Saving...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Reusable password field component
type PasswordFieldProps = {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  show: boolean;
  setShow: (show: boolean) => void;
  autoFocus?: boolean;
  margin?: 'none' | 'dense' | 'normal';
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  onChange,
  show,
  setShow,
  autoFocus = false,
  margin = 'normal',
}) => {
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <FormControl fullWidth margin={margin} size="small">
      <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
      <OutlinedInput
        size="small"
        label={label}
        type={show ? 'text' : 'password'}
        onChange={onChange}
        autoFocus={autoFocus}
        value={value}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={show ? 'hide the password' : 'display the password'}
              onClick={() => setShow(!show)}
              onMouseDown={handleMouseDownPassword}
              onMouseUp={handleMouseUpPassword}
              edge="end"
              size="small"
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
};

export default ChangePasswordDialog;
